/**
 * Centralized tab-level alert manager
 * ------------------------------------
 * Shared across multiple admin signals (pending payout SLA escalations,
 * new cashout requests, etc).
 *
 * Each producer registers its own *signal key* via `setTabSignal(key, level, count)`.
 * The manager picks the highest-priority active signal and reflects it in:
 *   - the document title prefix
 *   - the favicon (dynamically drawn $ glyph + colored alert dot)
 *
 * Levels (highest → lowest priority):
 *   'overdue'  red   🔴
 *   'critical' orange 🟠
 *   'info'     yellow 🟡  (e.g. there's a pending request to review)
 */
const LEVEL_PRIORITY = { overdue: 3, critical: 2, info: 1 };
const LEVEL_COLORS = {
    overdue:  { color: '#FF3B30', emoji: '🔴' },
    critical: { color: '#FF8C00', emoji: '🟠' },
    info:     { color: '#FFD700', emoji: '🟡' },
};

const _signals = new Map();   // key -> { level, count }
let _origFavicon = null;
let _origTitle = null;

const _captureOriginals = () => {
    if (_origTitle == null) _origTitle = document.title;
    if (_origFavicon == null) {
        const link = document.querySelector("link[rel*='icon']");
        _origFavicon = link ? link.href : null;
    }
};

const _drawAlertFavicon = (color) => {
    try {
        const c = document.createElement('canvas');
        c.width = 32; c.height = 32;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#0a0a0a';
        ctx.beginPath(); ctx.arc(16, 16, 15, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#FFD700';
        ctx.font = 'bold 20px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('$', 16, 17);
        ctx.fillStyle = color;
        ctx.beginPath(); ctx.arc(25, 7, 7, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = '#0a0a0a';
        ctx.lineWidth = 2;
        ctx.stroke();
        return c.toDataURL('image/png');
    } catch { return null; }
};

const _setFavicon = (href) => {
    if (!href) return;
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
    }
    link.href = href;
};

const _render = () => {
    _captureOriginals();

    // Aggregate: pick highest priority level; sum counts for that level + above
    let topLevel = null;
    let totalCount = 0;
    for (const { level, count } of _signals.values()) {
        if (!count) continue;
        if (!topLevel || LEVEL_PRIORITY[level] > LEVEL_PRIORITY[topLevel]) {
            topLevel = level;
        }
        totalCount += count;
    }

    if (!topLevel) {
        document.title = _origTitle || document.title;
        if (_origFavicon) _setFavicon(_origFavicon);
        return;
    }

    const meta = LEVEL_COLORS[topLevel];
    document.title = `${meta.emoji} (${totalCount}) ${_origTitle || ''}`.trim();
    const dataUrl = _drawAlertFavicon(meta.color);
    if (dataUrl) _setFavicon(dataUrl);
};

/**
 * Register / update a signal contribution to the global tab alert state.
 * @param {string} key      unique producer key (e.g. 'pending-payouts', 'new-cashouts')
 * @param {string|null} level  'overdue' | 'critical' | 'info' | null to clear
 * @param {number} count    how many items are in that level
 */
export const setTabSignal = (key, level, count = 0) => {
    if (!level || !count) {
        _signals.delete(key);
    } else {
        _signals.set(key, { level, count });
    }
    _render();
};

/** Soft 2-tone Web-Audio chime (subject to browser autoplay policy). */
let _audioCtx = null;
export const playChime = () => {
    try {
        if (!_audioCtx) {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            _audioCtx = new Ctx();
        }
        const ctx = _audioCtx;
        if (ctx.state === 'suspended') ctx.resume();
        const playTone = (freq, when, dur = 0.22, gain = 0.18) => {
            const osc = ctx.createOscillator();
            const g = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;
            g.gain.setValueAtTime(0.0001, ctx.currentTime + when);
            g.gain.exponentialRampToValueAtTime(gain, ctx.currentTime + when + 0.02);
            g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + when + dur);
            osc.connect(g); g.connect(ctx.destination);
            osc.start(ctx.currentTime + when);
            osc.stop(ctx.currentTime + when + dur + 0.05);
        };
        playTone(880, 0);
        playTone(1320, 0.15);
    } catch { /* ignore */ }
};
